/**
 * API Service for backend communication
 * Handles all API calls to the Verity User Auth backend
 */

import { CLIENT_APP_VERSION } from '@/constants/clientAppVersion';

// Hardcoded API URL for deployed Railway service
const API_BASE_URL = 'https://userauth.verityaudit.in/api/v1';

console.log('[API Service] Using API URL:', API_BASE_URL);

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

interface LoginRequest {
  email: string;
  password: string;
  application_type?: string;
}

interface LoginResponse {
  user: {
    id: string;
    user_name: string;
    email: string;
    firm_id: string;
    type: string;
  };
  accessToken: string;
  refreshToken: string;
}

interface Engagement {
  id: string;
  audit_client_id: string;
  status: string;
  is_default?: boolean;
  is_rf?: boolean;
  client_name?: string;
  engagement_name?: string;
  teamMembers?: Array<{
    id: string;
    user_name: string;
    email: string;
    role: string;
  }>;
  created_at?: string;
  updated_at?: string;
}

interface AuditClient {
  id: string;
  firm_id: string;
  client_name: string;
  status: string;
}

interface Firm {
  id: string;
  tenant_id: string;
  client_id: string;
  client_secret: string;
  site_hostname?: string;
  site_path?: string;
  admin_id?: string;
  admin_password?: string;
  confirmation_tool?: boolean;
  sampling_tool?: boolean;
}

class ApiService {
  private getAuthToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}${endpoint}`;
    console.log('[API Service] Making request to:', url);

    let response: Response;
    try {
      // For Electron apps, we can use 'no-cors' mode if CORS is an issue
      // But first try with normal fetch
      response = await fetch(url, {
        ...options,
        headers,
        // Don't set mode: 'cors' as it might cause issues in Electron
        // Electron can make cross-origin requests without CORS restrictions
      });
    } catch (error: any) {
      // Network error (connection refused, SSL, DNS, etc.)
      console.error('[API Service] Network error:', error);
      console.error('[API Service] Error name:', error.name);
      console.error('[API Service] Error message:', error.message);
      console.error('[API Service] Failed URL:', url);
      
      // Provide more specific error messages
      let errorMessage = `Failed to connect to the API server at ${url}\n\n`;
      
      if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        errorMessage += 'Possible causes:\n';
        errorMessage += '1. The API server is not accessible or down\n';
        errorMessage += '2. SSL certificate validation failed\n';
        errorMessage += '3. Network connectivity issues\n';
        errorMessage += '4. Firewall blocking the connection\n\n';
        errorMessage += 'Please verify:\n';
        errorMessage += `- The API is running at: ${url}\n`;
        errorMessage += '- Your internet connection is working\n';
        errorMessage += '- No firewall is blocking the connection';
      } else {
        errorMessage += `Error: ${error.message || 'Unknown network error occurred'}`;
      }
      
      throw new Error(errorMessage);
    }

    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    const hasJsonContent = contentType && contentType.includes('application/json');
    
    // Get response text first to check if it's empty
    const responseText = await response.text();
    
    if (!responseText || responseText.trim() === '') {
      console.error('[API Service] Empty response from server');
      throw new Error(`Empty response from server (Status: ${response.status} ${response.statusText}). The server may be experiencing issues.`);
    }

    // Try to parse JSON
    let data: ApiResponse<T>;
    try {
      if (!hasJsonContent) {
        console.warn('[API Service] Response is not JSON. Content-Type:', contentType);
        console.warn('[API Service] Response body:', responseText.substring(0, 500));
        throw new Error(`Server returned non-JSON response (${contentType}). This might be a CORS error or server configuration issue.`);
      }
      data = JSON.parse(responseText);
    } catch (parseError: any) {
      console.error('[API Service] JSON parse error:', parseError);
      console.error('[API Service] Response status:', response.status, response.statusText);
      console.error('[API Service] Response body:', responseText.substring(0, 500));
      throw new Error(`Failed to parse server response: ${parseError.message}. The server may have returned an error page or invalid JSON.`);
    }

    // Check if response indicates success
    if (!response.ok || !data.success) {
      const errorMessage = data.error?.message || `Request failed with status ${response.status}`;
      console.error('[API Service] API error:', errorMessage);
      throw new Error(errorMessage);
    }

    return data.data as T;
  }

  /**
   * Login with email and password
   * Note: Backend uses email, but we can accept username and try email first
   * Main software uses application_type: 'main'
   */
  async login(emailOrUsername: string, password: string, applicationType: string = 'main'): Promise<LoginResponse> {
    // Try as email first (backend expects email)
    // If username is provided, we might need to look it up, but for now assume it's email
    return this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email: emailOrUsername,
        password,
        application_type: applicationType,
        ...(applicationType === 'main'
          ? { client_version: CLIENT_APP_VERSION }
          : {}),
      }),
    });
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<LoginResponse['user']> {
    const response = await this.request<{ user: LoginResponse['user'] }>('/auth/me');
    return response.user;
  }

  /**
   * Send heartbeat to keep session alive
   * This updates the token's last_seen/updated_at timestamp on the backend
   * If heartbeat stops (e.g., force shutdown, crash), backend will auto-revoke token
   * 
   * Backend Requirements:
   * - Should update the refresh token's updated_at timestamp when /auth/me is called
   * - Should have a background job that auto-revokes tokens not updated in 5+ minutes
   * - This ensures tokens are revoked even if app crashes or system force shuts down
   */
  async heartbeat(): Promise<void> {
    try {
      // Use /auth/me endpoint which validates the token
      // Backend should update the refresh token's updated_at timestamp on each call
      await this.request('/auth/me');
    } catch (error: any) {
      // If heartbeat fails, log the error and re-throw so AuthContext can handle it
      // Don't crash the app - let AuthContext decide what to do
      const errorMessage = error?.message || 'Unknown error';
      console.warn('[Heartbeat API] Failed to send heartbeat:', errorMessage);
      throw error; // Re-throw so AuthContext can handle it
    }
  }

  /**
   * Logout user (revoke refresh token on backend)
   * POST /api/v1/auth/logout
   * @param refreshToken - Optional refresh token, will get from localStorage if not provided
   * @param useSendBeacon - Force use of sendBeacon (for page unload scenarios)
   */
  async logout(refreshToken?: string, useSendBeacon: boolean = false): Promise<void> {
    try {
      // Get refreshToken from parameter or localStorage
      const token = refreshToken || localStorage.getItem('refreshToken');
      
      if (!token) {
        console.warn('No refresh token available for logout');
        return;
      }

      const url = `${API_BASE_URL}/auth/logout`;
      const data = JSON.stringify({ refreshToken: token });

      // Use sendBeacon for page unload scenarios (more reliable than fetch)
      if (useSendBeacon && navigator.sendBeacon) {
        console.log('[Logout API] Using sendBeacon for logout...');
        // Try Blob first (backend handles blob/string requests)
        const blob = new Blob([data], { type: 'application/json' });
        let sent = navigator.sendBeacon(url, blob);
        console.log(`[Logout API] sendBeacon with Blob: ${sent ? 'success' : 'failed'}`);
        
        // If Blob fails, try with query parameter (backend supports this as fallback)
        if (!sent) {
          console.log('[Logout API] Trying sendBeacon with query parameter...');
          const urlWithQuery = `${url}?refreshToken=${encodeURIComponent(token)}`;
          sent = navigator.sendBeacon(urlWithQuery, new Blob());
          console.log(`[Logout API] sendBeacon with query: ${sent ? 'success' : 'failed'}`);
        }
        
        // If both fail, try fetch with keepalive as last resort
        if (!sent) {
          console.warn('[Logout API] sendBeacon failed, falling back to fetch with keepalive');
          try {
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: data,
              keepalive: true,
            });
            console.log(`[Logout API] Fetch with keepalive: ${response.ok ? 'success' : 'failed'}`);
          } catch (fetchError) {
            console.error('[Logout API] Fetch failed, trying synchronous XHR...', fetchError);
            // Last resort: synchronous XHR (deprecated but works for critical logout)
            try {
              const xhr = new XMLHttpRequest();
              xhr.open('POST', url, false); // false = synchronous
              xhr.setRequestHeader('Content-Type', 'application/json');
              xhr.send(data);
              console.log(`[Logout API] Synchronous XHR: ${xhr.status === 200 ? 'success' : 'failed'}`);
            } catch (xhrError) {
              console.error('[Logout API] All logout methods failed:', xhrError);
            }
          }
        } else {
          console.log('[Logout API] Logout request sent successfully via sendBeacon');
        }
      } else {
        // Normal fetch request (for manual logout, not page unload)
        await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: data,
        });
      }
    } catch (error: any) {
      // Log error but don't throw - logout should always succeed locally
      console.error('Error calling logout API:', error);
    }
  }

  /**
   * Get all engagements for the current user
   * @param filters - Optional filters object
   * @param filters.for_main - If true, only return engagements where main = true
   * @param filters.for_main_access - If true, only return engagements where main = true AND confirmation_tool = false
   * @param filters.for_confirmation_tool - If true, only return engagements where confirmation_tool = true
   */
  async getUserEngagements(filters?: { for_main?: boolean; for_main_access?: boolean; for_confirmation_tool?: boolean }): Promise<Engagement[]> {
    const params = new URLSearchParams();
    if (filters?.for_main) {
      params.append('for_main', 'true');
    }
    if (filters?.for_main_access) {
      params.append('for_main_access', 'true');
    }
    if (filters?.for_confirmation_tool) {
      params.append('for_confirmation_tool', 'true');
    }
    
    const queryString = params.toString();
    const url = queryString ? `/engagements?${queryString}` : '/engagements';
    const response = await this.request<Engagement[]>(url);
    return response;
  }

  /**
   * Get all clients for the current user (where user is partner or manager in engagements)
   * This gets clients through engagements where user has engagement_partner or engagement_manager role
   * Note: Clients are filtered by the backend to only include those where user is engagement_partner or engagement_manager
   * Frontend should further filter by status='active' if needed
   */
  async getUserClients(): Promise<AuditClient[]> {
    try {
      console.log('Fetching clients from /clients/my-clients endpoint...');
      // First try the my-clients endpoint
      // The backend returns: { success: true, data: { clients: [...] } }
      // So after request() extracts data, we get: { clients: [...] }
      const response = await this.request<{ clients: AuditClient[] }>('/clients/my-clients');
      console.log('Full response from my-clients:', JSON.stringify(response, null, 2));
      console.log('Response type:', typeof response);
      console.log('Is array?', Array.isArray(response));
      console.log('Response keys:', response ? Object.keys(response) : 'null');
      
      let clients: AuditClient[] = [];
      
      if (response) {
        // Check if response has clients property
        if ('clients' in response && Array.isArray(response.clients)) {
          console.log(`Found ${response.clients.length} clients in response.clients`);
          clients = response.clients;
        } else if (Array.isArray(response)) {
          // Handle case where response is array directly (shouldn't happen but just in case)
          console.log(`Found ${response.length} clients (array response)`);
          clients = response;
        } else {
          console.warn('Unexpected response structure:', response);
        }
      }
      
      // Filter to only show active clients where user is engagement_partner or engagement_manager
      // Backend already filters by engagement_partner/engagement_manager, we just need to filter by status
      const activeClients = clients.filter(client => {
        const isActive = client.status && client.status.toLowerCase() === 'active';
        if (!isActive) {
          console.log(`Filtering out client ${client.client_name} - status: ${client.status}`);
        }
        return isActive;
      });
      
      console.log(`Returning ${activeClients.length} active clients (out of ${clients.length} total)`);
      return activeClients;
    } catch (error: any) {
      console.error('Error fetching clients from my-clients endpoint:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      
      // Fallback: Get clients through engagements
      try {
        console.log('Trying fallback: getting clients through engagements...');
        // Get all engagements for the user
        const engagements = await this.getUserEngagements();
        console.log(`Found ${engagements.length} engagements`);
        
        // Extract unique client IDs from engagements where user is partner or manager
        const clientIds = [...new Set(
          engagements
            .filter(e => {
              // Check if user is engagement_partner or engagement_manager in this engagement
              if (!e.teamMembers || e.teamMembers.length === 0) {
                return false;
              }
              // Get current user to check their role
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              const userMember = e.teamMembers.find(tm => tm.id === user.id);
              return userMember && (
                userMember.role === 'engagement_partner' || 
                userMember.role === 'engagement_manager'
              );
            })
            .map(e => e.audit_client_id)
            .filter(Boolean)
        )];
        
        console.log(`Found ${clientIds.length} unique client IDs from engagements`);
        
        if (clientIds.length === 0) {
          return [];
        }
        
        // Get client details for each client ID
        try {
          const allClients = await this.getAllClients();
          const filteredClients = allClients.filter(client => clientIds.includes(client.id));
          console.log(`Filtered to ${filteredClients.length} clients`);
          return filteredClients;
        } catch (err) {
          // If we can't get all clients, return empty array
          console.error('Error getting clients:', err);
          return [];
        }
      } catch (error2: any) {
        console.error('Error getting user clients through engagements:', error2);
        return [];
      }
    }
  }

  /**
   * Get all clients for the firm (for engagement creation - only for partners/managers)
   */
  async getAllClients(): Promise<AuditClient[]> {
    try {
      const response = await this.request<{ clients: AuditClient[] } | AuditClient[]>('/clients');
      // Handle both response formats
      if (Array.isArray(response)) {
        return response;
      }
      return response.clients || [];
    } catch (error: any) {
      // If endpoint requires admin permissions, try the user clients endpoint
      if (error.message?.includes('permission') || error.message?.includes('Unauthorized')) {
        // Fallback to user clients
        return this.getUserClients();
      }
      throw error;
    }
  }

  /**
   * Get engagements for a specific client
   * Uses regular user engagements endpoint filtered by client_id / audit_client_id.
   */
  async getClientEngagements(clientId: string): Promise<Engagement[]> {
    const engagements = await this.request<Engagement[]>(
      `/engagements?client_id=${encodeURIComponent(clientId)}`
    );
    return engagements || [];
  }

  /**
   * Get archived engagements for a client that have not been rollforwarded yet.
   * Uses the dedicated /engagements/archived-for-rollforward endpoint which queries
   * Engagement directly (no engagement_users join required), so all archived
   * engagements for the client are returned regardless of team membership.
   * A client-side guard is applied as a safety net.
   */
  async getArchivedEngagementsForRollforward(clientId: string): Promise<Engagement[]> {
    const engagements = await this.request<Engagement[]>(
      `/engagements/archived-for-rollforward?client_id=${encodeURIComponent(clientId)}`
    );
    // Safety-net: ensure only Archived + not-yet-rollforwarded items are shown
    return (engagements || []).filter(
      (e) => e.status?.toLowerCase() === 'archived' && e.is_rf !== true
    );
  }

  /**
   * Get engagement by ID
   */
  async getEngagement(engagementId: string): Promise<Engagement> {
    const response = await this.request<{ engagement: Engagement }>(
      `/engagements/${encodeURIComponent(engagementId)}`
    );
    return response.engagement;
  }

  /**
   * Check whether an engagement name already exists for a client.
   * Call this BEFORE any SharePoint operations to fail fast without side effects.
   */
  async checkEngagementNameExists(clientId: string, engagementName: string): Promise<boolean> {
    const response = await this.request<{ exists: boolean }>(
      `/admin/clients/${encodeURIComponent(clientId)}/engagements/check-name?engagement_name=${encodeURIComponent(engagementName)}`
    );
    return response.exists;
  }

  /**
   * Update an engagement (PATCH /api/v1/engagements/:id)
   */
  async updateEngagement(engagementId: string, updateData: Partial<Engagement>): Promise<Engagement> {
    const response = await this.request<{ engagement: Engagement }>(
      `/engagements/${encodeURIComponent(engagementId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updateData),
      }
    );
    return response.engagement;
  }

  /**
   * Create a new engagement
   */
  async createEngagement(clientId: string, engagementName?: string): Promise<Engagement> {
    const response = await this.request<{ engagement: Engagement }>(
      `/admin/clients/${clientId}/engagements`,
      {
        method: 'POST',
        body: JSON.stringify({
          engagement_name: engagementName
        }),
      }
    );
    return response.engagement;
  }

  /**
   * Get SharePoint configuration for a specific engagement
   * GET /api/v1/sharepoint-config/:engagementId
   * Follows the chain: engagement → audit_client → firm
   * Returns decrypted credentials + correct doc_library and fy_year for the engagement.
   * Works with both UUID and numeric engagement IDs.
   */
  async getSharePointConfig(engagementId: string): Promise<{
    tenant_id: string;
    client_id: string;
    client_secret: string;
    site_hostname: string;
    site_path: string;
    doc_library: string;
    fy_year: string;
  }> {
    const response = await this.request<{
      tenant_id: string;
      client_id: string;
      client_secret: string;
      site_hostname: string;
      site_path: string;
      doc_library: string;
      fy_year: string;
    }>(`/sharepoint-config/${encodeURIComponent(engagementId)}`);
    return response;
  }

  /**
   * Get firm details for current user (for SharePoint config)
   * GET /api/v1/firms/me
   */
  async getFirm(): Promise<Firm> {
    try {
      const response = await this.request<{ firm: Firm }>('/firms/me');
      console.log('getFirm API response:', response);
      
      // The request method already extracts data from { success: true, data: {...} }
      // So response is { firm: Firm }
      if (!response || !response.firm) {
        console.error('Invalid firm response structure:', response);
        throw new Error('Invalid firm data received from server');
      }
      
      return response.firm;
    } catch (error: any) {
      console.error('Error in getFirm:', error);
      throw error;
    }
  }

  /**
   * Get all users for the firm
   * GET /api/v1/users
   */
  async getFirmUsers(): Promise<Array<{ id: string; user_name: string; email: string }>> {
    try {
      const response = await this.request<{ users: Array<{ id: string; user_name: string; email: string }> } | Array<{ id: string; user_name: string; email: string }>>('/users');
      if (Array.isArray(response)) {
        return response;
      }
      return response.users || [];
    } catch (error: any) {
      console.error('Error fetching firm users:', error);
      throw error;
    }
  }

  /**
   * Get engagement team members
   * GET /api/v1/engagements/:id/users
   * Returns users from engagement_users table for this engagement
   */
  async getEngagementTeam(engagementId: string): Promise<Array<any>> {
    try {
      console.log(`[API Service] Fetching engagement team for engagement: ${engagementId}`);
      const response = await this.request<{ team?: Array<any>; users?: Array<any> }>(`/engagements/${engagementId}/users`);
      console.log('[API Service] Engagement team response:', response);
      const team = response.team ?? response.users ?? [];
      console.log(`[API Service] Returning ${team.length} team members from engagement_users`);
      return team;
    } catch (error: any) {
      console.error('[API Service] Error fetching engagement team:', error);
      throw error;
    }
  }

  /**
   * Add user to engagement
   * POST /api/v1/engagements/:id/users
   */
  async addUserToEngagement(engagementId: string, userId: string, role: string = 'associate'): Promise<void> {
    try {
      await this.request(`/engagements/${engagementId}/users`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          role: role
        }),
      });
    } catch (error: any) {
      console.error('Error adding user to engagement:', error);
      throw error;
    }
  }

  /**
   * Remove user from engagement
   * DELETE /api/v1/engagements/:id/users/:userId
   */
  async removeUserFromEngagement(engagementId: string, userId: string): Promise<void> {
    try {
      await this.request(`/engagements/${engagementId}/users/${userId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('Error removing user from engagement:', error);
      throw error;
    }
  }

  /**
   * Get engagement partner and manager emails for a client
   * Gets emails from the default engagement or any previous engagement for the client
   */
  async getClientEngagementEmails(clientId: string): Promise<string[]> {
    try {
      // Get engagements for the client
      const engagements = await this.getClientEngagements(clientId);
      
      // Find an engagement with team members (prefer non-default, but use default if needed)
      let engagementWithTeam = engagements.find(e => e.teamMembers && e.teamMembers.length > 0);
      
      // If no engagement found, try to get the default engagement
      if (!engagementWithTeam) {
        // Try to get engagement by fetching with include team members
        // For now, we'll try to get any engagement and then fetch its team
        if (engagements.length > 0) {
          const firstEngagement = engagements[0];
          try {
            const fullEngagement = await this.getEngagement(firstEngagement.id);
            if (fullEngagement.teamMembers && fullEngagement.teamMembers.length > 0) {
              engagementWithTeam = fullEngagement;
            }
          } catch (err) {
            console.warn('Could not fetch full engagement details:', err);
          }
        }
      }

      if (!engagementWithTeam || !engagementWithTeam.teamMembers) {
        console.warn('No engagement with team members found for client:', clientId);
        return [];
      }

      // Extract emails from engagement_partner and engagement_manager
      const emails: string[] = [];
      
      for (const member of engagementWithTeam.teamMembers) {
        if (
          (member.role === 'engagement_partner' || member.role === 'engagement_manager') &&
          member.email
        ) {
          emails.push(member.email);
        }
      }

      // Remove duplicates
      return [...new Set(emails)];
    } catch (error: any) {
      console.error('Error getting client engagement emails:', error);
      // Return empty array on error - don't block engagement creation
      return [];
    }
  }
}

export const apiService = new ApiService();
export type { LoginResponse, Engagement, AuditClient, Firm };

